import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const KEY = "cc_cart";

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const initial = useRef(true);
  const debounceRef = useRef(null);

  // persist locally
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  // pull server cart on login
  useEffect(() => {
    if (!user) return;
    api.get("/cart").then(({ data }) => {
      const serverItems = data?.items || [];
      // merge with current local: union by id, server wins on duplicates
      setItems((cur) => {
        const map = new Map();
        cur.forEach((i) => map.set(i.id, i));
        serverItems.forEach((i) => map.set(i.id, i));
        return Array.from(map.values());
      });
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user?.id]);

  // debounced server sync (logged-in users only)
  useEffect(() => {
    if (initial.current) { initial.current = false; return; }
    if (!user) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post("/cart/sync", { items }).catch(() => {});
    }, 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [items, user?.id]);

  const add = (item) => {
    setItems((cur) => (cur.find((i) => i.id === item.id) ? cur : [...cur, item]));
  };
  const remove = (id) => setItems((cur) => cur.filter((i) => i.id !== id));
  const clear = () => setItems([]);
  const total = items.reduce((s, i) => s + Number(i.price || 0), 0);

  return (
    <CartContext.Provider value={{ items, add, remove, clear, total }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
